import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAlerts } from "context/AlertContext";
import useBroadcastChannel from "hooks/useBroadcastChannel";
import { services } from "services";
import {
  getLogoutRedirectUrl,
  setAuthCookieForDev,
  setOriginalUriToRestore,
} from "utils/auth";
import { AlertSeverity } from "utils/constants/alerts.constants";
import { LOGOUT_BROADCAST_CHANNEL_ID } from "utils/constants/broadcastChannel.constants";
import { TLogoutContextValue } from "./LogoutContext.types";

export const LogoutContext = createContext<TLogoutContextValue | undefined>(
  undefined,
);

export const LogoutProvider = (props: PropsWithChildren) => {
  const { showAlert } = useAlerts();
  const { t } = useTranslation("common", { keyPrefix: "notifications" });
  const [isUserLoggingOut, setIsUserLoggingOut] = useState<boolean>(false);

  useBroadcastChannel({
    name: LOGOUT_BROADCAST_CHANNEL_ID,
    handler: (event) => {
      if (event.data.logoutRedirectUrl) {
        window.location.href = event.data.logoutRedirectUrl;
      }
    },
  });

  const logout = useCallback<TLogoutContextValue["logout"]>(
    async (options) => {
      setIsUserLoggingOut(true);

      if (options.shouldRestorePath) {
        setOriginalUriToRestore();
      }

      try {
        setAuthCookieForDev("");
        const { data } = await services.auth.logout();
        const logoutRedirectUrl = getLogoutRedirectUrl(data);

        // The other open tabs don't need to perform the logout request, since the session's cookies
        // will already be cleared and then token will have been added to the banlist on the backend.
        if (options.shouldLogoutEverywhere) {
          const broadcastChannel = new BroadcastChannel(
            LOGOUT_BROADCAST_CHANNEL_ID,
          );

          broadcastChannel.postMessage({ logoutRedirectUrl });
          broadcastChannel.close();
        }
      } catch (error) {
        showAlert({
          severity: AlertSeverity.Error,
          message: t("logoutFailed"),
        });

        throw error;
      }
    },
    [showAlert, t, setIsUserLoggingOut],
  );

  const value = useMemo(
    () => ({ logout, isUserLoggingOut }),
    [logout, isUserLoggingOut],
  );

  return (
    <LogoutContext.Provider value={value}>
      {props.children}
    </LogoutContext.Provider>
  );
};
